import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { Status } from '../provider/stepper.provider';
import {
  activeStepperDelay,
  inactiveStepperDuration,
  stepperDelay,
  stepperDuration,
  stepperLineWidth,
} from '../stepper.styles';

const isExpanded = (stepStatus: Status) =>
  stepStatus === 'active' || stepStatus === 'currActive' || stepStatus === 'errorActive';

export const horizontalContainer = css`
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

/**
 * Width depends on the number of steps at runtime, so it is driven by the
 * `--stepper-horizontal-step-width` custom property set on the element.
 */
export const horizontalStep = css`
  display: flex;
  flex-direction: column;
  width: var(--stepper-horizontal-step-width);
`;

/* -------------------------------------------------------------------------- */
/* step box                                                                   */
/* -------------------------------------------------------------------------- */

const stepBoxBase = css`
  height: 32px;
  margin-bottom: ${theme.spacing(2)};
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  transform: translateX(calc(50% - 12px));
  .step-line-horizontal.inactive,
  .step-line-horizontal.error {
    height: 2px;
    position: absolute;
    left: 24px;
    width: calc(100% - 24px);
    background-color: ${theme.colors.neutral40};
  }
`;

const stepBoxExpanded = css`
  .step-line-horizontal.inactive,
  .step-line-horizontal.error {
    left: 32px;
  }
`;

const stepBoxActive = css`
  transform: translateX(calc(50% - 16px));
`;

export const stepBox = (stepStatus: Status) =>
  cx(stepBoxBase, isExpanded(stepStatus) && stepBoxExpanded, stepStatus === 'active' && stepBoxActive);

/* -------------------------------------------------------------------------- */
/* step line (horizontal)                                                     */
/* -------------------------------------------------------------------------- */

const stepLineHorizontalBase = css`
  height: 2px;
  position: absolute;
  background-color: ${theme.colors.neutral40};
`;

const stepLineHorizontalCompleted = css`
  background-color: ${theme.colors.primary50};
`;

const stepLineHorizontalActive = css`
  background-color: ${theme.colors.primary50};
  top: calc(50% - 1px);
  position: absolute;
`;

const stepLineHorizontalCritical = css`
  background-color: ${theme.colors.status.critical};
`;

export const stepLineHorizontal = (stepStatus: Status) =>
  cx(
    stepLineHorizontalBase,
    stepStatus === 'completed' && stepLineHorizontalCompleted,
    isExpanded(stepStatus) && stepLineHorizontalActive,
    (stepStatus === 'errorActive' || stepStatus === 'error') && stepLineHorizontalCritical
  );

/* -------------------------------------------------------------------------- */
/* step number (horizontal)                                                   */
/* -------------------------------------------------------------------------- */

const stepNumHorizontalBase = css`
  color: ${theme.colors.primary50};
  background-color: ${theme.colors.white};
  border: 2px solid ${theme.colors.neutral40};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  left: ${theme.spacing(-4)};
  p,
  h2 {
    line-height: 1;
    padding-top: 2px;
    font-size: ${theme.fontSize(16)};
    text-align: center;
    color: ${theme.colors.neutral40};
    margin: 0;
  }
`;

const stepNumHorizontalInactiveTransition = css`
  transition-delay: 0s;
  transition-duration: ${inactiveStepperDuration};
  transition-property: background-color, border, width, height, padding, transform, margin-left;
  p,
  h2 {
    transition-delay: 0s;
    transition-duration: ${inactiveStepperDuration};
    transition-property: color, line-height, font-size;
  }
`;

const stepNumHorizontalCompleted = css`
  color: ${theme.colors.primary50};
  background-color: ${theme.colors.white};
  border: 2px solid ${theme.colors.primary50};
  width: 24px;
  height: 24px;
  padding: ${stepperLineWidth};
  cursor: pointer;
  p,
  h2 {
    color: ${theme.colors.neutral40};
  }
`;

const stepNumHorizontalActive = css`
  background-color: ${theme.colors.primary50};
  border: 2px solid ${theme.colors.primary50};
  width: 32px;
  min-width: 32px;
  height: 32px;
  min-height: 32px;
  padding: 2px;
  transition-delay: ${stepperDelay};
  transition-duration: ${stepperDuration};
  transition-property: background-color, border, width, height, padding, transform;
  p,
  h2 {
    padding-top: 3px;
    font-size: ${theme.fontSize(18)};
    font-weight: ${theme.font.weight.bold};
    color: ${theme.colors.white};
    transition-delay: ${activeStepperDelay};
    transition-duration: ${stepperDuration};
    transition-property: color, line-height;
  }
`;

const stepNumHorizontalError = css`
  background-color: ${theme.colors.status.critical};
  border: 2px solid ${theme.colors.status.critical};
  p,
  h2 {
    color: ${theme.colors.white};
  }
`;

export const stepNumHorizontal = (stepStatus: Status) =>
  cx(
    stepNumHorizontalBase,
    (stepStatus === 'inactive' || stepStatus === 'error') && stepNumHorizontalInactiveTransition,
    stepStatus === 'completed' && stepNumHorizontalCompleted,
    isExpanded(stepStatus) && stepNumHorizontalActive,
    (stepStatus === 'errorActive' || stepStatus === 'error') && stepNumHorizontalError
  );

/* -------------------------------------------------------------------------- */
/* step title text (horizontal)                                               */
/* -------------------------------------------------------------------------- */

const stepTitleTextBase = css`
  margin: ${theme.spacing(0, 1)};
  line-height: 1.25;
  text-align: center;
  min-height: 40px;
`;

const stepTitleTextHidden = css`
  display: none;
`;

const stepTitleTextHoverable = css`
  &:hover {
    cursor: pointer;
    color: ${theme.colors.primary50};
  }
`;

export const stepTitleText = (isMobile: boolean, stepStatus: Status) =>
  cx(stepTitleTextBase, isMobile && stepTitleTextHidden, stepStatus !== 'inactive' && stepTitleTextHoverable);
